/**
 * Concrete routing/isochrone providers (M8-B). Both implement the vendor-neutral
 * `RoutingProvider` interface; a deployment swaps in a road-network provider
 * (Valhalla/OSRM adapter, or a metered vendor as fallback) without touching any
 * caller. Attribution is carried NON-REMOVABLY, exactly like the tile source.
 *
 *  • haversineRoutingProvider — the OSS-first DEFAULT. `optimizeRoute` is a real
 *    nearest-neighbor + 2-opt solve over a great-circle matrix (fully
 *    client-computable, no external call). `getIsochrones` returns the labeled
 *    straight-line approximation (a road-network isochrone needs an engine).
 *
 *  • createStubIsochroneProvider — the drive-time default. Same interface; its
 *    `getIsochrones` is the explicit radius-buffer stub (approximate, tunable
 *    assumed speed) and it delegates route solving to the same haversine core.
 *    Named to make the "stub until a routing engine is configured" posture loud
 *    at the call site.
 */

import { approxIsochrones } from "./isochrone";
import { optimizeStops } from "./solve";
import {
  DEFAULT_ROUTE_SPEED_KMH,
  MAX_ROUTE_STOPS,
  RouteStopLimitError,
  type Isochrone,
  type IsochroneBand,
  type LngLat,
  type OptimizeResult,
  type RouteStop,
  type RoutingProvider,
} from "./types";

export interface RoutingProviderOptions {
  /** Assumed average travel speed (km/h) for the OSS distance→time estimate. */
  speedKmh?: number;
  /** Attribution string carried through non-removably (defaults per provider). */
  attribution?: string;
}

const OSS_ATTRIBUTION = "Route estimate — great-circle distance (no routing engine configured)";
const STUB_ISOCHRONE_ATTRIBUTION =
  "Drive-time estimate — straight-line approximation (configure a routing engine for road-network isochrones)";

function assertUnderCap(stops: RouteStop[]): void {
  if (stops.length > MAX_ROUTE_STOPS) throw new RouteStopLimitError(stops.length);
}

/**
 * OSS-first default provider. Real route optimization now; isochrones are the
 * labeled straight-line approximation until a routing engine is bound.
 */
export function haversineRoutingProvider(opts?: RoutingProviderOptions): RoutingProvider {
  const speedKmh = opts?.speedKmh ?? DEFAULT_ROUTE_SPEED_KMH;
  return {
    attribution: opts?.attribution ?? OSS_ATTRIBUTION,
    async getIsochrones(origin: LngLat, bands: IsochroneBand[]): Promise<Isochrone[]> {
      return approxIsochrones(origin, bands, { speedKmh });
    },
    async optimizeRoute(stops: RouteStop[]): Promise<OptimizeResult> {
      assertUnderCap(stops);
      return optimizeStops(stops, speedKmh);
    },
  };
}

/**
 * Drive-time default provider — the explicit isochrone STUB. Identical route
 * solving to the OSS provider; its isochrones are the clearly-labeled
 * radius-buffer approximation with a tunable assumed speed.
 */
export function createStubIsochroneProvider(opts?: RoutingProviderOptions): RoutingProvider {
  const speedKmh = opts?.speedKmh ?? DEFAULT_ROUTE_SPEED_KMH;
  return {
    attribution: opts?.attribution ?? STUB_ISOCHRONE_ATTRIBUTION,
    async getIsochrones(origin: LngLat, bands: IsochroneBand[]): Promise<Isochrone[]> {
      return approxIsochrones(origin, bands, { speedKmh });
    },
    async optimizeRoute(stops: RouteStop[]): Promise<OptimizeResult> {
      assertUnderCap(stops);
      return optimizeStops(stops, speedKmh);
    },
  };
}
